import Router from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  Badge,
  FlexContainer,
  MoreOptions,
  TimeIndicator
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { Icon } from 'components/common/icons';
import {
  CertificatesAddCalendarEvent,
  CertificatesAddCourse,
  CertificatesAddPath,
  CertificatesProductItem,
  CertificatesCreate,
  CertificatesDelete,
  CertificatesSetDefault
} from '.';

const CertificatesList = () => {
  const {
    language,
    messages,
    serverConfig: { defaultCertificateTemplateId }
  } = useReduxState();

  const { data, loading, updateData } = useApiData({
    url: '/admin/certificate-templates'
  });

  const calendarEvents = data?.calendarEvents;
  const courses = data?.courses;
  const paths = data?.paths;

  let templates = data?.templates || [];

  templates = templates.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1));

  const handleTemplateClick = (ev) => {
    const as = ev.currentTarget.getAttribute('data-as');
    const href = ev.currentTarget.getAttribute('data-href');
    Router.push(href, as);
  };

  const Enabled = () => (
    <Icon icon="check-circle" style={{ fontSize: '1.3rem' }} />
  );

  return (
    <div className="templates">
      <Box
        headerContent={<CertificatesCreate updateTemplates={updateData} />}
        loading={loading}
        padding={0}
        title={messages.siteCertificatesTemplates}
      >
        <BoxTable>
          <BoxTableHeader>
            <BoxTableCell>{messages.commonTitle}</BoxTableCell>
            <BoxTableCell>{messages.commonDefault}</BoxTableCell>
            <BoxTableCell>{'Courses'}</BoxTableCell>
            <BoxTableCell>{'Paths'}</BoxTableCell>
            <BoxTableCell>{'Calendar events'}</BoxTableCell>
            <BoxTableCell>{'Selected courses'}</BoxTableCell>
            <BoxTableCell>{'Selected paths'}</BoxTableCell>
            <BoxTableCell>{'Selected calendar events'}</BoxTableCell>
            <BoxTableCell></BoxTableCell>
          </BoxTableHeader>

          <BoxTableRow>
            <BoxTableCell>
              {messages.siteCertificatesDefaultCertificate}
            </BoxTableCell>
            <BoxTableCell>
              {!defaultCertificateTemplateId ? (
                <Badge text={messages.commonDefault} />
              ) : null}
            </BoxTableCell>
            <BoxTableCell>
              <Enabled />
            </BoxTableCell>
            <BoxTableCell>
              <Enabled />
            </BoxTableCell>
            <BoxTableCell>
              <Enabled />
            </BoxTableCell>
            <BoxTableCell />
            <BoxTableCell />
            <BoxTableCell />
            <BoxTableCell shrink={true}>
              <MoreOptions>
                <CertificatesSetDefault templateId={null} />
              </MoreOptions>
            </BoxTableCell>
          </BoxTableRow>

          {!loading && templates && templates.length
            ? templates.map((template) => (
                <BoxTableRow
                  key={template.id}
                  data-as={`/admin/settings/certificates/${template.id}`}
                  data-href="/admin/settings/certificates/[templateId]"
                  onClick={handleTemplateClick}
                >
                  <BoxTableCell>
                    {template.title}
                    {template.updatedAt ? (
                      <div style={{ fontSize: '1.2rem', fontWeight: 400 }}>
                        {`${messages.commonUpdated} `}
                        <TimeIndicator
                          language={language}
                          timeString={template.updatedAt}
                        />
                      </div>
                    ) : (
                      '-'
                    )}
                  </BoxTableCell>
                  <BoxTableCell>
                    {defaultCertificateTemplateId === template.id ? (
                      <Badge text={messages.commonDefault} />
                    ) : null}
                  </BoxTableCell>
                  <BoxTableCell>
                    {template.courseIsSupported ? <Enabled /> : null}
                  </BoxTableCell>
                  <BoxTableCell>
                    {template.pathIsSupported ? <Enabled /> : null}
                  </BoxTableCell>
                  <BoxTableCell>
                    {template.calendarEventIsSupported ? <Enabled /> : null}
                  </BoxTableCell>
                  <BoxTableCell>
                    <FlexContainer
                      style={{ gap: '5px 10px', flexWrap: 'wrap' }}
                    >
                      {template.courses.map((course) => (
                        <CertificatesProductItem
                          course={course}
                          key={course.id}
                          templateId={template.id}
                          updateTemplates={updateData}
                        />
                      ))}
                    </FlexContainer>
                  </BoxTableCell>
                  <BoxTableCell>
                    <FlexContainer
                      style={{ gap: '5px 10px', flexWrap: 'wrap' }}
                    >
                      {template.paths.map((path) => (
                        <CertificatesProductItem
                          path={path}
                          key={path.id}
                          templateId={template.id}
                          updateTemplates={updateData}
                        />
                      ))}
                    </FlexContainer>
                  </BoxTableCell>
                  <BoxTableCell>
                    <FlexContainer
                      style={{ gap: '5px 10px', flexWrap: 'wrap' }}
                    >
                      {template.calendarEvents.map((calendarEvent) => (
                        <CertificatesProductItem
                          calendarEvent={calendarEvent}
                          key={calendarEvent.id}
                          templateId={template.id}
                          updateTemplates={updateData}
                        />
                      ))}
                    </FlexContainer>
                  </BoxTableCell>
                  <BoxTableCell shrink={true}>
                    <MoreOptions>
                      <CertificatesSetDefault templateId={template.id} />
                      <CertificatesAddCourse
                        courses={courses.filter(
                          (course) =>
                            !template.courses.find((c) => c.id === course.id)
                        )}
                        templateId={template.id}
                        updateTemplates={updateData}
                      />
                      <CertificatesAddPath
                        paths={paths.filter(
                          (path) =>
                            !template.paths.find((p) => p.id === path.id)
                        )}
                        templateId={template.id}
                        updateTemplates={updateData}
                      />
                      <CertificatesAddCalendarEvent
                        calendarEvents={calendarEvents.filter(
                          (event) =>
                            !template.calendarEvents.find(
                              (e) => e.id === event.id
                            )
                        )}
                        templateId={template.id}
                        updateTemplates={updateData}
                      />
                      <CertificatesDelete
                        templateId={template.id}
                        updateTemplates={updateData}
                      />
                    </MoreOptions>
                  </BoxTableCell>
                </BoxTableRow>
              ))
            : null}
        </BoxTable>
      </Box>
    </div>
  );
};

export default CertificatesList;
